import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCockpit, type Cockpit, type CockpitRange } from '@/features/command-center';
import { useTheme } from '@/hooks/useTheme';
import { useSession } from '@/stores/session';

const RANGES: { value: CockpitRange; label: string }[] = [
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'ytd', label: 'YTD' },
];

// Validated categorical palette (dataviz skill) — light / dark steps.
const C = {
  income: { light: '#2a78d6', dark: '#3987e5' },
  expense: { light: '#eb6834', dark: '#d95926' },
  bar: { light: '#2a78d6', dark: '#3987e5' },
  grid: { light: '#e1e0d9', dark: '#2c2c2a' },
  axis: '#898781',
};

const n = (v: number | string | null | undefined) => Number(v ?? 0);

function usd(v: number | string | null | undefined, compact = false): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : 0,
  }).format(n(v));
}

const STAGE_LABEL: Record<string, string> = {
  new: 'New',
  qualified: 'Qualified',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
};
const stageLabel = (s: string) => STAGE_LABEL[s] ?? s.charAt(0).toUpperCase() + s.slice(1);

// Attention row → where it links + whether it's a hard alert (red dot).
const ATTENTION_META: Record<string, { to: string; alert?: boolean }> = {
  overdue_invoices: { to: '/finance/billing', alert: true },
  overdue_work_items: { to: '/projects', alert: true },
  overdue_milestones: { to: '/projects', alert: true },
  over_budget_projects: { to: '/projects' },
  proposals_awaiting: { to: '/crm/proposals' },
};

export function Dashboard() {
  const user = useSession((s) => s.user);
  const org = useSession((s) => s.organizations.find((o) => o.slug === s.activeOrgSlug));
  const [range, setRange] = useState<CockpitRange>('month');
  const { data, isPending } = useCockpit(range);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm">
            Welcome back{user ? `, ${user.first_name || user.username}` : ''}
            {org ? ` — ${org.name}` : ''}.
          </p>
        </div>
        <div className="bg-muted/60 flex items-center rounded-md p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              aria-pressed={range === r.value}
              className={cn(
                'rounded px-2.5 py-1 text-sm',
                range === r.value
                  ? 'bg-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isPending || !data ? (
        <DashboardSkeleton />
      ) : (
        <>
          {data.attention.length > 0 ? <AttentionCard items={data.attention} /> : null}

          {data.pipeline ? (
            <Pillar title="Win — pipeline">
              <Tile label="Open deals" value={String(data.pipeline.open_deals)} to="/crm/deals" />
              <Tile
                label="Weighted pipeline"
                value={usd(data.pipeline.open_pipeline_weighted_approx, true)}
                hint="approx · mixed currency"
                to="/crm/deals"
              />
              <Tile
                label="Won (period)"
                value={usd(data.pipeline.won_value_approx, true)}
                hint={`${data.pipeline.won_count} deal${data.pipeline.won_count === 1 ? '' : 's'}`}
                to="/crm/deals"
              />
              <Tile
                label="Proposals awaiting"
                value={String(data.pipeline.proposals_awaiting)}
                to="/crm/proposals"
              />
            </Pillar>
          ) : null}

          <Pillar title="Deliver — projects">
            <Tile label="Active projects" value={String(data.delivery.active_projects)} to="/projects" />
            <Tile label="Tracked hours" value={String(n(data.delivery.tracked_hours))} to="/projects" />
            <Tile
              label="Overdue items"
              value={String(data.delivery.overdue_work_items)}
              tone={data.delivery.overdue_work_items > 0 ? 'alert' : undefined}
              to="/projects"
            />
            <Tile
              label="Upcoming milestones"
              value={String(data.delivery.upcoming_milestones)}
              hint="next 14 days"
              to="/projects"
            />
          </Pillar>

          {data.finance ? (
            <Pillar title="Get paid — finance">
              <Tile label="Net (USD)" value={usd(data.finance.net_base_usd)} hint="income − expenses" />
              <Tile
                label="AR outstanding"
                value={usd(data.finance.outstanding_ar_base_usd, true)}
                to="/finance/billing"
              />
              <Tile
                label="AR overdue"
                value={usd(data.finance.overdue_ar_base_usd, true)}
                tone={n(data.finance.overdue_ar_base_usd) > 0 ? 'alert' : undefined}
                to="/finance/billing"
              />
              <Tile
                label="Commissions pending"
                value={usd(data.finance.commissions_pending_base_usd, true)}
                to="/finance/commissions"
              />
            </Pillar>
          ) : null}

          {data.people ? (
            <Pillar title="Grow — people">
              <Tile label="Headcount" value={String(data.people.headcount)} to="/hrm" />
              <Tile
                label="On leave this week"
                value={String(data.people.on_leave_this_week)}
                to="/hrm"
              />
            </Pillar>
          ) : null}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {data.finance ? <IncomeExpenseChart monthly={data.finance.monthly} /> : null}
            {data.pipeline ? <PipelineChart byStage={data.pipeline.by_stage} /> : null}
            {data.finance ? <ArAgingChart aging={data.finance.ar_aging} /> : null}
          </div>
        </>
      )}
    </div>
  );
}

function Pillar({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
        {title}
      </h2>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{children}</div>
    </section>
  );
}

function Tile({
  label,
  value,
  hint,
  to,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  to?: string;
  tone?: 'alert';
}) {
  const body = (
    <Card className={cn('h-full transition-colors', to && 'hover:border-primary/40')}>
      <CardHeader className="gap-1 pb-2">
        <p className="text-muted-foreground text-sm">{label}</p>
        <p
          className={cn(
            'text-2xl font-semibold tabular-nums',
            tone === 'alert' && 'text-destructive',
          )}
        >
          {value}
        </p>
        {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
      </CardHeader>
    </Card>
  );
  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}

function AttentionCard({ items }: { items: Cockpit['attention'] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Needs attention</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {items.map((a) => {
          const meta = ATTENTION_META[a.type] ?? { to: '/' };
          return (
            <Link
              key={a.type}
              to={meta.to}
              className="hover:bg-muted flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
            >
              <span
                className={cn(
                  'size-2 rounded-full',
                  meta.alert ? 'bg-destructive' : 'bg-amber-400',
                )}
              />
              {a.label}
              <span className="text-muted-foreground tabular-nums">
                {a.amount_base_usd != null ? usd(a.amount_base_usd, true) : a.count}
              </span>
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ChartCard({
  title,
  empty,
  children,
}: {
  title: string;
  empty: boolean;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="text-muted-foreground flex h-52 items-center justify-center text-sm">
            No data yet.
          </div>
        ) : (
          <div className="h-52">{children}</div>
        )}
      </CardContent>
    </Card>
  );
}

function IncomeExpenseChart({ monthly }: { monthly: NonNullable<Cockpit['finance']>['monthly'] }) {
  const { isDarkMode } = useTheme();
  const mode = isDarkMode ? 'dark' : 'light';
  const rows = monthly.slice(-12).map((m) => ({
    month: new Date(m.month).toLocaleDateString(undefined, { month: 'short' }),
    income: n(m.income),
    expense: n(m.expense),
  }));
  return (
    <ChartCard title="Income vs expenses" empty={rows.length === 0}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={C.grid[mode]} />
          <XAxis
            dataKey="month"
            tick={{ fill: C.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fill: C.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => usd(v, true)}
            width={56}
          />
          <Tooltip formatter={(v: number) => usd(v)} cursor={{ fill: C.grid[mode], opacity: 0.4 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="income" name="Income" fill={C.income[mode]} radius={[4, 4, 0, 0]} />
          <Bar dataKey="expense" name="Expenses" fill={C.expense[mode]} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function PipelineChart({ byStage }: { byStage: NonNullable<Cockpit['pipeline']>['by_stage'] }) {
  const { isDarkMode } = useTheme();
  const mode = isDarkMode ? 'dark' : 'light';
  const rows = byStage.map((s) => ({
    stage: stageLabel(s.stage),
    value: n(s.value_approx),
    count: s.count,
  }));
  return (
    <ChartCard title="Open pipeline by stage" empty={rows.length === 0}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid horizontal={false} stroke={C.grid[mode]} />
          <XAxis
            type="number"
            tick={{ fill: C.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => usd(v, true)}
          />
          <YAxis
            type="category"
            dataKey="stage"
            tick={{ fill: C.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            width={84}
          />
          <Tooltip formatter={(v: number) => usd(v)} cursor={{ fill: C.grid[mode], opacity: 0.4 }} />
          <Bar dataKey="value" name="Value" fill={C.bar[mode]} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function ArAgingChart({ aging }: { aging: NonNullable<Cockpit['finance']>['ar_aging'] }) {
  const { isDarkMode } = useTheme();
  const mode = isDarkMode ? 'dark' : 'light';
  const rows = aging.map((a) => ({ bucket: a.bucket, amount: n(a.amount_base_usd) }));
  const empty = rows.every((r) => r.amount === 0);
  // Older buckets read "hotter" — a light→dark blue ordinal ramp.
  const RAMP = ['#86b6ef', '#3987e5', '#1c5cab', '#104281'];
  return (
    <ChartCard title="AR aging" empty={empty}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={C.grid[mode]} />
          <XAxis
            dataKey="bucket"
            tick={{ fill: C.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fill: C.axis, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => usd(v, true)}
            width={56}
          />
          <Tooltip formatter={(v: number) => usd(v)} cursor={{ fill: C.grid[mode], opacity: 0.4 }} />
          <Bar dataKey="amount" name="Outstanding" radius={[4, 4, 0, 0]}>
            {rows.map((_, i) => (
              <Cell key={i} fill={RAMP[Math.min(i, RAMP.length - 1)]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}
